import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VStack, Heading, Text, Spinner, Button, useDisclosure } from '@chakra-ui/react';
import { getYblDowntimeList, createYblDowntime, getRdaMetaData } from '@store/actions/rdaDashboardActions';
import GenericCard from '@components/GenericCard';
import { CARD_TYPE, ROLES } from '@constants';
import ModalLayout from '@components/Modal/ModalLayout';
import useAuthRole from '@hooks/useAuthRole';
import DynamicInputModal from '@components/DynamicInputModal';
import { YBL_DOWNTIME_FORM_CONFIG } from './yblDowntimeFormConfig';
import dayjs from 'dayjs';

// Utility to get downtime status and color
const getDowntimeStatus = (startTime, endTime) => {
  const now = dayjs();
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  if (now.isAfter(end)) return { text: 'Ended', color: 'red.500' };
  if (now.isBefore(start)) return { text: 'Upcoming', color: 'orange.400' };
  if (now.isAfter(start) && now.isBefore(end)) return { text: 'Ongoing', color: 'green.400' };
  return { text: 'Unknown', color: 'gray.400' };
};

const YblDowntime = () => {
  const dispatch = useDispatch();
  const yblDowntimeList = useSelector((state) => state?.rdaDashboard?.yblDowntimeList);
  const metaData = useSelector((state) => state?.rdaDashboard?.metaData);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [modalState, setModalState] = useState({
    description: '',
    startTime: '',
    endTime: '',
    isActive: 'true'
  });
  const { hasAnyAuthority } = useAuthRole();

  useEffect(() => {
    dispatch(getYblDowntimeList());
    dispatch(getRdaMetaData());
  }, [dispatch]);

  const handleOpen = () => {
    setModalState({ description: '', startTime: '', endTime: '', isActive: 'true' });
    onOpen();
  };

  const handleFormSubmit = (formData) => {
    setIsSubmitLoading(true);
    const payload = {
      ...formData,
      isActive: formData.isActive === 'true',
      startTime: formData.startTime ? dayjs(formData.startTime).utc().toISOString() : undefined,
      endTime: formData.endTime ? dayjs(formData.endTime).utc().toISOString() : undefined,
    };
    dispatch(createYblDowntime({
      payload,
      onSuccess: () => {
        dispatch(getYblDowntimeList());
        onClose();
      },
      onComplete: () => setIsSubmitLoading(false)
    }));
  };

  // Dynamically build form config with metaData
  const dynamicFormConfig = YBL_DOWNTIME_FORM_CONFIG.map(field => {
    if (field.key === 'partner' && metaData?.data?.partners) {
      return {
        ...field,
        options: metaData.data.partners.map(p => ({ value: p, label: p }))
      };
    }
    if ((field.key === 'rail' || field.key === 'transferType') && metaData?.data?.transferTypes) {
      return {
        ...field,
        options: metaData.data.transferTypes.map(t => ({ value: t, label: t }))
      };
    }
    return field;
  });

  return (
    <VStack alignItems="stretch" gap={3}>
      <Heading size="md" color="white">Downtime</Heading>
      {hasAnyAuthority(ROLES.ADMIN_ROLE) && (
        <Button color={'black'} colorScheme="brand" alignSelf="flex-start" onClick={handleOpen} mb={2}>
          Add Downtime
        </Button>
      )}
      {hasAnyAuthority(ROLES.ADMIN_ROLE) && (
        <ModalLayout isOpen={isOpen} onClose={onClose}>
          <DynamicInputModal
            title="Add Downtime"
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleFormSubmit}
            fields={dynamicFormConfig}
            isLoading={isSubmitLoading}
          />
        </ModalLayout>
      )}
      {yblDowntimeList.isLoading && <Spinner color="#81ebab" />}
      {yblDowntimeList.isError && <Text color="red.400">{yblDowntimeList.error || 'Failed to load downtime list.'}</Text>}
      {!yblDowntimeList.isLoading && !yblDowntimeList.isError && yblDowntimeList.data && yblDowntimeList.data.length === 0 && (
        <Text color="gray.400">No downtime records found.</Text>
      )}
      {!yblDowntimeList.isLoading && !yblDowntimeList.isError && yblDowntimeList.data && yblDowntimeList.data.length > 0 && (
        <VStack alignItems="stretch" gap={2}>
          {yblDowntimeList.data.map((item) => {
            const status = getDowntimeStatus(item.startTime, item.endTime);
            return (
              <GenericCard
                key={item.id}
                cardData={item}
                cardType={CARD_TYPE.YBL_DOWNTIME_CARD}
                status={status}
              />
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default YblDowntime; 